import moment from "moment"

import { BRAND_NAME } from "../../i18n/config"
import { getDisplayPrice, isSoldOut } from "../common/ticket-price"

// schema.org JSON-LD for search engines. Built from the same API payload the
// page renders, so what a crawler reads and what a visitor sees cannot drift.
//
// Everything here is optional-in, omitted-out: a property is only emitted when
// the API actually supplied it. Google drops the whole rich result for an Event
// with a malformed date or an empty offer, so a missing key is safer than a
// placeholder.

const compact = object =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined && value !== null && value !== ""))

// The `*_utc` fields carry an offset ("2026-11-28T03:00:00+0000"); the plain
// ones are venue wall-clock with none, which a crawler would read as UTC.
const isoDate = value => {
  if (!value) return undefined
  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed.toISOString() : undefined
}

const placeOf = venue => {
  if (!venue?.name) return undefined

  const address = compact({
    streetAddress: [venue.street, venue.street_number].filter(Boolean).join(" "),
    addressLocality: venue.city,
    addressRegion: venue.state,
    postalCode: venue.postal_code,
    addressCountry: venue.country ? String(venue.country).toUpperCase() : undefined
  })

  const latitude = parseFloat(venue.latitude)
  const longitude = parseFloat(venue.longitude)

  return compact({
    "@type": "Place",
    name: venue.name,
    address: Object.keys(address).length ? { "@type": "PostalAddress", ...address } : undefined,
    geo:
      Number.isFinite(latitude) && Number.isFinite(longitude)
        ? { "@type": "GeoCoordinates", latitude, longitude }
        : undefined
  })
}

const offersOf = ({ tickets = [], tables = [] }, event, url) =>
  [...tickets, ...tables]
    .filter(item => item?.name)
    .map(item =>
      compact({
        "@type": "Offer",
        name: item.name,
        price: parseFloat(getDisplayPrice(item).toFixed(2)),
        priceCurrency: event.currency?.currency,
        availability: isSoldOut(item) ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
        validFrom: isoDate(event.sales_start_utc),
        url
      })
    )

export const eventJsonLd = ({ event, venue, tickets, tables, url, siteUrl }) => {
  if (!event?.name) return null

  const startDate = isoDate(event.start_date_utc)
  if (!startDate) return null

  const offers = offersOf({ tickets, tables }, event, url)
  const images = [event.flyer_image, event.image_full_url].filter(Boolean)

  return compact({
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.name,
    description: event.description_plain || undefined,
    image: images.length ? [...new Set(images)] : undefined,
    url,
    startDate,
    endDate: isoDate(event.end_date_utc),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: event.is_online_event
      ? "https://schema.org/OnlineEventAttendanceMode"
      : "https://schema.org/OfflineEventAttendanceMode",
    location: event.is_online_event ? { "@type": "VirtualLocation", url } : placeOf(venue),
    offers: offers.length ? offers : undefined,
    organizer: { "@type": "Organization", name: event.brand_name || BRAND_NAME, url: siteUrl },
    typicalAgeRange: event.minimum_age ? `${event.minimum_age}+` : undefined
  })
}

// The listing page: tells crawlers these links are a set of events and gives
// the storefront an Organization identity to attach them to.
export const eventListJsonLd = ({ events = [], siteUrl, listUrl }) => {
  const items = events
    .filter(event => event?.slug && !event.redirect_url)
    .map((event, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: event.name,
      url: `${siteUrl}/events/${event.slug}/`
    }))

  return [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: BRAND_NAME,
      url: listUrl
    },
    ...(items.length
      ? [{ "@context": "https://schema.org", "@type": "ItemList", itemListElement: items }]
      : [])
  ]
}
